/**
 * ContactPage — public Contact Us with best-in-class UX.
 *
 * Layout:
 *  · Hero with response promise + brand chip
 *  · Two-column: form on left (category chips, honeypot, char counters,
 *    validation), contact channels on right (email, phone, WhatsApp,
 *    LinkedIn, founder DM, address map link)
 *  · Response-time promise strip
 *  · FAQ accordion
 *  · Success state — animated confirmation with next-step chips
 */
import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail, MessageCircle, Phone, MapPin, Send, Loader2, CheckCircle2, Sparkles,
  MessagesSquare, Linkedin, Twitter, Instagram, ArrowRight, HelpCircle,
  ShieldAlert, CreditCard, Zap, ChevronDown, Home, ArrowLeft,
} from "lucide-react";
import { COMPANY, ADDRESS_LINES } from "@/lib/company";
import { Footer } from "@/components/legal/Footer";
import { SEO } from "@/components/shared/SEO";
import { BrandMark } from "@/components/shared/BrandMark";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

// Categories — server-side enum matches this list.
const CATEGORIES: Array<{ id: string; label: string; icon: React.ReactNode; blurb: string }> = [
  { id: "sales",       label: "Sales",        icon: <Zap className="h-3.5 w-3.5" />,          blurb: "Talk to a real human before you subscribe" },
  { id: "support",     label: "Product help", icon: <MessagesSquare className="h-3.5 w-3.5" />, blurb: "Bugs, questions, how-to" },
  { id: "billing",     label: "Billing",      icon: <CreditCard className="h-3.5 w-3.5" />,   blurb: "Invoices, refunds, plan changes" },
  { id: "security",    label: "Security",     icon: <ShieldAlert className="h-3.5 w-3.5" />,  blurb: "Vulnerability reports (48 hr)" },
  { id: "press",       label: "Press",        icon: <Sparkles className="h-3.5 w-3.5" />,     blurb: "Media, interviews, quotes" },
  { id: "partnership", label: "Partnership",  icon: <ArrowRight className="h-3.5 w-3.5" />,   blurb: "Integrations, resellers, revenue share" },
  { id: "feedback",    label: "Feedback",     icon: <Sparkles className="h-3.5 w-3.5" />,     blurb: "Ideas, requests, gripes" },
  { id: "other",       label: "Something else", icon: <HelpCircle className="h-3.5 w-3.5" />, blurb: "Anything not above" },
];

const FAQ = [
  {
    q: "How fast will I get a reply?",
    a: "We aim for one business day on every message, faster if you put [URGENT] in the subject. Security reports get a first-touch response within 48 hours.",
  },
  {
    q: "Can I talk to a real human?",
    a: "Yes. Every reply comes from a person on the SociBaba team in Bengaluru — never a bot. Prefer voice? Call +91 97399 59565 Mon-Fri 10 AM to 5 PM IST.",
  },
  {
    q: "Do you support WhatsApp?",
    a: "Absolutely. Ping us on WhatsApp — click the WhatsApp chip on the right and we'll reply from the shared support line during business hours.",
  },
  {
    q: "Is my message private?",
    a: "Your message goes straight to the SociBaba support inbox. We never share, sell, or train models on your conversation. See our Privacy Policy for the full picture.",
  },
];

interface FormState {
  name: string;
  email: string;
  phone: string;
  category: string;
  subject: string;
  message: string;
  website: string; // honeypot
}

const INIT: FormState = {
  name: "", email: "", phone: "", category: "support", subject: "", message: "", website: "",
};

export const ContactPage: React.FC = () => {
  const [form, setForm] = useState<FormState>(INIT);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ submission_id: string; message: string } | null>(null);

  const canSubmit =
    form.name.trim().length >= 1 &&
    /.+@.+\..+/.test(form.email) &&
    form.subject.trim().length >= 3 &&
    form.message.trim().length >= 20 &&
    !submitting;

  const set = (k: keyof FormState) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const backend = process.env.REACT_APP_BACKEND_URL || "";
      const r = await fetch(`${backend}/api/contact/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source_url: typeof window !== "undefined" ? window.location.href : undefined,
        }),
      });
      const j = await r.json().catch(() => ({}));
      if (!r.ok) {
        toast.error(j?.detail || "Could not send. Try again in a moment.");
        return;
      }
      setDone({ submission_id: j.submission_id, message: j.message });
    } catch (err: any) {
      toast.error(err?.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col overflow-x-hidden" data-testid="page-contact">
      <SEO
        title="Contact SociBaba — real humans, one-day reply"
        description="Talk to the SociBaba team. Sales, support, billing, security, press or partnership. We reply within one business day. Call, email, WhatsApp or use the form."
        path="/contact"
        type="article"
        keywords={["contact SociBaba", "SociBaba support", "SociBaba sales", "WhatsApp SociBaba", "SociBaba press"]}
      />

      {/* Header */}
      <header className="border-b border-slate-200 bg-white/95 backdrop-blur sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="contact-home-link">
            <BrandMark
              showIcon
              iconClassName="h-9 w-9 mr-2 inline-block align-middle dark:invert"
              className="font-serif-display text-lg font-bold tracking-tight leading-none flex items-center"
            />
          </Link>
          <div className="flex items-center gap-2">
            <Link
              to="/"
              className="hidden sm:inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 text-sm font-medium px-3.5 py-1.5"
            >
              <Home className="h-4 w-4" /> Home
            </Link>
            <a
              href={COMPANY.whatsappUrl}
              target="_blank" rel="noopener noreferrer"
              data-testid="contact-header-whatsapp"
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-3.5 py-1.5"
            >
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-fuchsia-50 via-white to-amber-50 border-b border-slate-100">
        <div className="absolute inset-0 opacity-30 pointer-events-none [background:radial-gradient(500px_circle_at_20%_50%,rgba(236,72,153,0.18),transparent_60%),radial-gradient(500px_circle_at_80%_50%,rgba(251,146,60,0.18),transparent_60%)]" />
        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 pt-14 pb-10 sm:pt-16 sm:pb-14 text-center">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-white/80 backdrop-blur border border-slate-200 px-3 py-1 text-[11px] uppercase tracking-widest font-semibold text-fuchsia-700 mb-4">
            <Sparkles className="h-3 w-3" /> One-business-day reply · From a real human
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900">
            Talk to the team.<br />
            <span className="bg-gradient-to-br from-fuchsia-600 via-orange-500 to-amber-500 bg-clip-text text-transparent">
              We actually reply.
            </span>
          </h1>
          <p className="mt-4 text-slate-600 text-base sm:text-lg max-w-xl mx-auto">
            Pick your channel below or drop us a note — sales, support, billing, security, press, partnership or something else entirely.
          </p>
        </div>
      </section>

      {/* Body */}
      <main className="flex-1 bg-white">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 sm:py-14">
          {/* Back crumb */}
          <div className="mb-6">
            <Link
              to="/"
              data-testid="contact-back"
              className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-800 text-sm font-medium px-3.5 py-1.5"
            >
              <ArrowLeft className="h-4 w-4" /> Back to home
            </Link>
          </div>

          <div className="grid gap-10 lg:grid-cols-[1.15fr_.85fr]">
            {/* ── Form ── */}
            <div>
              {done ? (
                <SuccessState submissionId={done.submission_id} message={done.message} />
              ) : (
                <form onSubmit={submit} className="space-y-5" data-testid="contact-form" noValidate>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Field label="Your name" required>
                      <Input
                        data-testid="contact-name"
                        placeholder="What should we call you?"
                        value={form.name}
                        onChange={set("name")}
                        maxLength={120}
                        autoComplete="name"
                        required
                      />
                    </Field>
                    <Field label="Email" required>
                      <Input
                        data-testid="contact-email"
                        type="email"
                        placeholder="you@work.com"
                        value={form.email}
                        onChange={set("email")}
                        maxLength={200}
                        autoComplete="email"
                        required
                      />
                    </Field>
                  </div>

                  <Field label="Phone (optional)" hint="We'll only call if you ask us to.">
                    <Input
                      data-testid="contact-phone"
                      type="tel"
                      placeholder="+91 …"
                      value={form.phone}
                      onChange={set("phone")}
                      maxLength={32}
                      autoComplete="tel"
                    />
                  </Field>

                  <Field label="What's it about?" required>
                    <div className="flex flex-wrap gap-1.5" data-testid="contact-category-chips">
                      {CATEGORIES.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          data-testid={`contact-cat-${c.id}`}
                          onClick={() => setForm((f) => ({ ...f, category: c.id }))}
                          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                            form.category === c.id
                              ? "bg-fuchsia-600 text-white border-fuchsia-600 shadow-sm"
                              : "bg-white text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                          }`}
                          title={c.blurb}
                        >
                          {c.icon}{c.label}
                        </button>
                      ))}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1.5" data-testid="contact-cat-blurb">
                      {CATEGORIES.find((x) => x.id === form.category)?.blurb}
                    </div>
                  </Field>

                  <Field label="Subject" required>
                    <Input
                      data-testid="contact-subject"
                      placeholder="One line — what's on your mind?"
                      value={form.subject}
                      onChange={set("subject")}
                      maxLength={160}
                      required
                    />
                    <div className="text-[10px] text-slate-400 text-right mt-0.5">
                      {form.subject.length}/160
                    </div>
                  </Field>

                  <Field label="Message" required hint={`Min 20 characters · ${form.message.length}/4000`}>
                    <Textarea
                      data-testid="contact-message"
                      placeholder="Give us as much context as you can. The more you write, the better the reply."
                      value={form.message}
                      onChange={set("message")}
                      rows={7}
                      maxLength={4000}
                      required
                    />
                  </Field>

                  {/* Honeypot — hidden from users, catches bots */}
                  <div className="hidden" aria-hidden>
                    <label>
                      Do not fill this out
                      <input
                        type="text"
                        tabIndex={-1}
                        autoComplete="off"
                        value={form.website}
                        onChange={set("website")}
                      />
                    </label>
                  </div>

                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-[11px] text-slate-500 flex items-center gap-1.5">
                      <ShieldAlert className="h-3 w-3" />
                      Your message is private — we don&apos;t share, sell or train models on it.
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-submit"
                      disabled={!canSubmit}
                      className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-black disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-semibold px-5 py-2.5 transition-colors"
                    >
                      {submitting ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                      Send message
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* ── Contact channels ── */}
            <aside className="space-y-3" data-testid="contact-channels">
              <ChannelCard
                testid="contact-channel-whatsapp"
                href={COMPANY.whatsappUrl}
                external
                icon={<MessageCircle className="h-5 w-5 text-white" />}
                iconBg="bg-emerald-500"
                title="WhatsApp"
                subtitle={COMPANY.customerCarePhone}
                tag="Fastest"
                tagClass="bg-emerald-50 text-emerald-700 border-emerald-200"
              />
              <ChannelCard
                testid="contact-channel-email"
                href={`mailto:${COMPANY.supportEmail}`}
                icon={<Mail className="h-5 w-5 text-white" />}
                iconBg="bg-fuchsia-600"
                title="Email"
                subtitle={COMPANY.supportEmail}
                tag="1 day"
                tagClass="bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200"
              />
              <ChannelCard
                testid="contact-channel-phone"
                href={`tel:${COMPANY.customerCarePhoneRaw}`}
                icon={<Phone className="h-5 w-5 text-white" />}
                iconBg="bg-orange-500"
                title="Call"
                subtitle={`${COMPANY.customerCarePhone} · ${COMPANY.supportHours}`}
                tag="Live"
                tagClass="bg-orange-50 text-orange-700 border-orange-200"
              />
              <ChannelCard
                testid="contact-channel-linkedin"
                href={COMPANY.linkedinUrl}
                external
                icon={<Linkedin className="h-5 w-5 text-white" />}
                iconBg="bg-[#0A66C2]"
                title="LinkedIn"
                subtitle="Company page"
              />
              <ChannelCard
                testid="contact-channel-founder"
                href={COMPANY.founderLinkedinUrl}
                external
                icon={<Sparkles className="h-5 w-5 text-white" />}
                iconBg="bg-slate-900"
                title="DM the founder"
                subtitle="Yesuraj G · direct line"
              />

              {/* Registered office */}
              <div
                className="mt-6 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
                data-testid="contact-office"
              >
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-slate-600 mt-0.5 flex-shrink-0" />
                  <div className="text-xs leading-snug">
                    <div className="font-semibold text-slate-900 mb-0.5">{COMPANY.legalName}</div>
                    {ADDRESS_LINES.map((l, i) => (
                      <div key={i} className="text-slate-600">{l}</div>
                    ))}
                    <div className="mt-2 text-[10px] uppercase tracking-widest text-slate-500 font-mono">
                      CIN {COMPANY.cin} · GST {COMPANY.gst}
                    </div>
                  </div>
                </div>
              </div>

              {/* Social row */}
              <div className="flex items-center gap-2 pt-1">
                <a
                  href={COMPANY.xUrl}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="contact-social-x"
                  className="h-9 w-9 rounded-full border border-slate-200 hover:border-slate-400 flex items-center justify-center bg-white transition-colors"
                  aria-label="Follow SociBaba on X"
                >
                  <Twitter className="h-4 w-4 text-slate-700" />
                </a>
                <a
                  href={COMPANY.instagramUrl}
                  target="_blank" rel="noopener noreferrer"
                  data-testid="contact-social-instagram"
                  className="h-9 w-9 rounded-full border border-slate-200 hover:border-slate-400 flex items-center justify-center bg-white transition-colors"
                  aria-label="Follow SociBaba on Instagram"
                >
                  <Instagram className="h-4 w-4 text-slate-700" />
                </a>
              </div>
            </aside>
          </div>

          {/* FAQ */}
          <section
            data-testid="contact-faq"
            className="mt-16 max-w-3xl mx-auto"
          >
            <div className="text-center mb-6">
              <div className="text-[11px] uppercase tracking-widest text-slate-500">Common questions</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight mt-1 text-slate-900">
                Before you write to us…
              </h2>
            </div>
            <div className="space-y-2">
              {FAQ.map((item, i) => (
                <FaqItem key={i} idx={i} q={item.q} a={item.a} />
              ))}
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// ────── Field / Channel / Faq / Success helpers ─────────────────────────

const Field: React.FC<{
  label: string; required?: boolean; hint?: string; children: React.ReactNode;
}> = ({ label, required, hint, children }) => (
  <div>
    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5">
      {label}
      {required && <span className="text-fuchsia-600 ml-1">*</span>}
    </label>
    {children}
    {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
  </div>
);

const ChannelCard: React.FC<{
  testid?: string; href: string; external?: boolean;
  icon: React.ReactNode; iconBg: string; title: string; subtitle: string;
  tag?: string; tagClass?: string;
}> = ({ testid, href, external, icon, iconBg, title, subtitle, tag, tagClass }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm p-3 transition group"
  >
    <div className={`h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center flex-shrink-0`}>
      {icon}
    </div>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-semibold text-slate-900 text-sm">{title}</span>
        {tag && (
          <span className={`text-[9px] uppercase tracking-widest font-semibold rounded-full border px-1.5 py-0.5 ${tagClass}`}>
            {tag}
          </span>
        )}
      </div>
      <div className="text-xs text-slate-500 truncate">{subtitle}</div>
    </div>
    <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-slate-600 transition-colors flex-shrink-0" />
  </a>
);

const FaqItem: React.FC<{ idx: number; q: string; a: string }> = ({ idx, q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`contact-faq-${idx}`}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50/60"
        aria-expanded={open}
      >
        <span className="font-medium text-slate-900 text-sm">{q}</span>
        <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            key="body"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed">{a}</div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SuccessState: React.FC<{ submissionId: string; message: string }> = ({ submissionId, message }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    data-testid="contact-success"
    className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-amber-50 p-6 sm:p-8"
  >
    <div className="flex items-start gap-3">
      <div className="h-11 w-11 rounded-full bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
        <CheckCircle2 className="h-6 w-6" />
      </div>
      <div className="flex-1">
        <div className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
          Message received.
        </div>
        <p className="text-slate-700 mt-1.5">{message}</p>
        <div className="mt-3 text-[11px] uppercase tracking-widest text-slate-500 font-mono">
          Ref · {submissionId}
        </div>
        <div className="mt-5 flex flex-wrap gap-2">
          <a
            href={COMPANY.whatsappUrl}
            target="_blank" rel="noopener noreferrer"
            data-testid="contact-success-whatsapp"
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold px-4 py-2 transition-colors"
          >
            <MessageCircle className="h-4 w-4" /> WhatsApp us if urgent
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-800 text-sm font-semibold px-4 py-2 transition-colors"
          >
            Back to home
          </Link>
        </div>
      </div>
    </div>
  </motion.div>
);

export default ContactPage;
